import { useTranslation } from '../presentation/common/useTranslation.js';
import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { shouldShowLevelUp } from '../utils/levelUp';
import './DialogProvider.css';
import './LevelUpPopup.css';

export default function LevelUpPopup({ profile, blocked = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [celebration, setCelebration] = useState(null);
  const seen = useRef(new Map());
  const dialog = useRef(null);
  useEffect(() => {
    if (!profile?.userId || !Number.isInteger(profile.level)) return;
    const key = `goodvale_level_seen_${profile.userId}`;
    let previous = seen.current.get(key);
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) previous = Number(stored);
    } catch { /* Keep working when browser storage is unavailable. */ }
    const latestLevel = Math.max(Number.isInteger(previous) ? previous : 0, profile.level);
    seen.current.set(key, latestLevel);
    try { localStorage.setItem(key, String(latestLevel)); } catch { /* In-memory fallback. */ }
    if (profile.levelUpPopupsEnabled === false) setCelebration(null);
    else if (shouldShowLevelUp(previous, profile.level, profile.levelUpPopupsEnabled)) {
      setCelebration({ userId: profile.userId, level: profile.level });
    }
  }, [profile]);

  const level = !blocked && celebration?.userId === profile?.userId && profile?.levelUpPopupsEnabled !== false
    ? celebration?.level : null;
  useEffect(() => {
    if (!level) return;
    const element = dialog.current;
    const previousFocus = document.activeElement;
    element.showModal();
    return () => {
      element.close();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [level]);

  if (!level) return null;
  return createPortal(<dialog ref={dialog} className="gw-dialog level-up-dialog" aria-labelledby="level-up-title" aria-describedby="level-up-description" onCancel={() => setCelebration(null)}>
    <div className="gw-dialog-content level-up-celebration text-center">
      <div className="level-up-eyebrow"><Sparkles size={15} aria-hidden="true" />{t('level_up.eyebrow')}<Sparkles size={15} aria-hidden="true" /></div>
      <h2 id="level-up-title">{t('level_up.title')}</h2>
      <div className="level-up-crest" data-digits={String(level).length} aria-label={t('level_up.level_label', { level })}>
        <Sparkles className="level-up-crest-sparkle" size={23} aria-hidden="true" />
        <span className="level-up-crest-caption">{t('level_up.level')}</span>
        <strong>{level}</strong>
      </div>
      <p id="level-up-description" className="gw-dialog-description level-up-message">{t('level_up.message', { level })}</p>
    </div>
    <div className="gw-dialog-actions">
      <button className="gw-dialog-secondary" onClick={() => setCelebration(null)}>{t('level_up.continue')}</button>
      <button autoFocus className="gw-dialog-primary" onClick={() => { setCelebration(null); navigate('/profile/redemptions'); }}>{t('level_up.find_partners')}</button>
    </div>
  </dialog>, document.body);
}
